import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { Field } from 'formik'
import { isEmpty, isNil, toNumber } from 'lodash'
import { IconCalendarCog, IconCheck, IconSparkles } from '@tabler/icons-react'
import classNames from 'classnames'
import format from 'date-fns/format'
import { differenceInDays } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { determineGradient } from '@/components/helpers'
import { FilterTypeDropdown } from '@/components/filters/types/FilterTypeDropdown'
import { FilterTypeSelect } from '@/components/filters/types/FilterTypeSelect'
import { SuggestedQuestions } from '@/components/interactions/SuggestedQuestions'
import { Pill } from '@/components/pills/Pill'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { FilterTypeSearchableDropdown } from '@/components/filters/types/FilterTypeSearchableDropdown'
import { RECENT_QUESTIONS_COACHING_FEATURE_NAME } from '@/utils/constants'
import { getChannelTypeOptions, hasAnyOmnichannelAccess } from '@/utils/helpers'
import { INBOX_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { updateSavedFilter } from '@/reducers/savedFilters/savedFilters.actions'
import { fetchActiveCoachingForAgent } from '@/reducers/tasks/tasks.actions'
import { fetchPlaybooks } from '@/reducers/playbooks/playbooks.actions'
import './CoachingSelfService.scss'
import { getLoadingPhrases, getCoachingQuestionsWithChannel } from '@/reducers/tasks/tasks.helpers'

const CoachingSelfServiceFormComponent = ({
  loadingPhraseIndex,
  values,
  loading,
  suggestedAgents = [],
  handleSubmit,
  setFieldValue,
  data,
}) => {
  const dispatch = useDispatch()
  const [dateInvalid, setDateInvalid] = useState(false)
  const [activeCoachingConfig, setActiveCoachingConfig] = useState(null)
  const textAreaRef = useRef(null)
  const filteredAgents = data.agents.filter(
    (a) => !suggestedAgents.some((sa) => sa.value === a.value)
  )
  const { savedFilterList } = useSelector((state) => state.savedFilters)
  const { selectedSavedFilterId } = useSelector((state) => state.tasks)
  const selectedSavedFilter =
    savedFilterList[INBOX_FILTER_TYPE].find((filter) => filter.uuid === selectedSavedFilterId) || {}
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const isBaltoAdmin = currentUserOrgId === 1
  const isOmniChannelEnabled = hasAnyOmnichannelAccess(activeChannels, isBaltoAdmin)
  const playbooks = useSelector((state) => state.playbooks.playbooks) || []
  const playbookOptions = playbooks.map((playbook) => ({
    value: playbook.cid,
    label: playbook.name,
  }))
  const formDisabledContent = (values) => {
    if (loading.coachingRequest) {
      return 'Generating...'
    }
    if (!values.agent) {
      return 'Please select an agent'
    }
    if (!values.question) {
      return 'Please describe your coaching focus'
    }
    if (!values.channel) {
      return 'Please select a channel'
    }
    return ''
  }
  const handleUpdateFilter = (agentIdToAdd) => {
    const agentToAdd = data.agents.find((agent) => agent.value === agentIdToAdd)

    if (!isEmpty(selectedSavedFilter) && agentToAdd) {
      dispatch(
        updateSavedFilter(
          selectedSavedFilterId,
          {
            name: selectedSavedFilter.name,
            isDefault: selectedSavedFilter.is_default,
            filterType: INBOX_FILTER_TYPE,
            agents: [...selectedSavedFilter.filters.agents, agentToAdd],
            tags: [...selectedSavedFilter.filters.tags],
          },
          INBOX_FILTER_TYPE
        )
      )
      setFieldValue('agent', null)
    }
  }

  const handleUpdateRange = ({ selection }) => {
    const currentStartDate = format(selection.startDate, "yyyy-MM-dd'T00:00:00'xxx")
    const currentEndDate = format(selection.endDate, "yyyy-MM-dd'T23:59:59'xxx")

    const datesDiff = differenceInDays(selection.endDate, selection.startDate)

    if (datesDiff > 62) {
      setDateInvalid(true)
    } else {
      setDateInvalid(false)
    }

    setFieldValue('startDate', currentStartDate)
    setFieldValue('endDate', currentEndDate)
  }

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto'
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`
    }
  }, [textAreaRef.current, values.question])

  useEffect(() => {
    setFieldValue('agent', null)
  }, [selectedSavedFilterId])

  useEffect(() => {
    const fetchConfig = async () => {
      if (values.agent) {
        const response = await fetchActiveCoachingForAgent(dispatch, values.agent)
        setActiveCoachingConfig(response)
      } else {
        setActiveCoachingConfig(null)
      }
    }
    fetchConfig()
  }, [values.agent])

  useEffect(() => {
    if (currentUserOrgId) {
      dispatch(fetchPlaybooks(currentUserOrgId))
    }
  }, [dispatch, currentUserOrgId])

  return (
    <>
      <div className={classNames({ 'coaching-selfservice-columns': activeCoachingConfig })}>
        <div className="flex flex-col coaching-selfservice-main-col">
          <form
            className="coaching-selfservice-form"
            data-testid="coaching-selfservice-form"
            onSubmit={handleSubmit}
          >
            <div className="mb">
              <div>
                <div className="label-style">Coach an Agent</div>
                <div className="flex-align-center small-gap flex-wrap mb">
                  {!isEmpty(suggestedAgents) &&
                    suggestedAgents.map((suggestedAgent, index) => {
                      const isSelected = values.agent === suggestedAgent.value
                      const gradient =
                        suggestedAgent.color || determineGradient(toNumber(index || 0))

                      return (
                        <Pill
                          key={suggestedAgent.value}
                          clickable
                          onClick={() => {
                            if (values.agent === suggestedAgent.value) {
                              setFieldValue('agent', null)
                            } else {
                              setFieldValue('agent', suggestedAgent.value)
                            }
                          }}
                          data-testid={`${suggestedAgent.value}-pill`}
                          selected={isSelected}
                          gradient={!isSelected && gradient}
                        >
                          {isSelected && <IconCheck className="status-brand" />}
                          {suggestedAgent.label}
                        </Pill>
                      )
                    })}
                  <FilterTypeSelect
                    customPlaceholder={
                      !isEmpty(selectedSavedFilter) ? 'Add an Agent' : 'Select an Agent'
                    }
                    showLabel={false}
                    autoFocus
                    loading={loading.agents}
                    schema={{ accessor: 'agent', label: 'Agent', options: filteredAgents }}
                    value={isEmpty(selectedSavedFilter) ? values.agent : null}
                    updateSearch={(accessor, value) => {
                      setFieldValue(accessor, value)
                    }}
                    handleUpdateFilter={handleUpdateFilter}
                    data-testid="coaching-selfservice-ad-hoc-agent-select"
                  />
                </div>
              </div>
            </div>
            <div className="label-style">Describe Your Coaching Focus</div>
            <div className={classNames({ 'animated-gradient-border': loading.coachingRequest })}>
              <div className="fake-textarea">
                <Field
                  name="question"
                  placeholder="What do you want to coach on?"
                  as="textarea"
                  rows={3}
                  innerRef={textAreaRef}
                  data-testid="textArea-question"
                />

                <div className="flex-align-center flex-space-between">
                  <div className="flex-align-center flex-space-between small-gap">
                    <DateRangePickerReusable
                      data-testid="filter-type-date-range"
                      startDate={values.startDate}
                      endDate={values.endDate}
                      onChange={handleUpdateRange}
                      shouldNotResetOnCancel
                      error={dateInvalid}
                      disableSaveButton={dateInvalid}
                      friendlyLabel
                      basicLabel
                      isFilterPill
                      icon={<IconCalendarCog />}
                      saveOrCloseOnClickPreset
                    />
                    {isOmniChannelEnabled && (
                      <FilterTypeDropdown
                        dataTestId="coaching-channel-filter"
                        schema={{
                          accessor: 'channel',
                          label: 'Channel',
                          options: getChannelTypeOptions(activeChannels, isBaltoAdmin),
                        }}
                        value={values.channel}
                        updateSearch={(accessor, value) =>
                          setFieldValue('channel', isEmpty(value) ? undefined : value)
                        }
                      />
                    )}
                    <FilterTypeSearchableDropdown
                      schema={{
                        accessor: 'selectedPlaybooks',
                        label: 'Playbook',
                        options: playbookOptions,
                      }}
                      value={values.selectedPlaybooks}
                      updateSearch={(accessor, updatedValues) => {
                        setFieldValue(accessor, updatedValues)
                      }}
                      selectedOptionFirst={false}
                      isClearable={!isEmpty(values.selectedPlaybooks)}
                      isMultiSelect
                      showResultsHeader
                      dataTestId="coaching-playbook-dropdown"
                    />
                  </div>
                  {loading.coachingRequest ? (
                    <Button destructive type="submit" data-testid="coaching-selfservice-stop">
                      Stop
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      brand
                      disabled={!values.agent || !values.question || !values.channel}
                      disabledTooltip={formDisabledContent(values)}
                      loading={loading.coachingRequest}
                      data-testid="coaching-selfservice-submit"
                      className="no-wrap"
                    >
                      <IconSparkles />
                      Generate Session
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {!isNil(loadingPhraseIndex) && (
              <p className="loading-phrase" style={{ maxWidth: '650px' }}>
                {getLoadingPhrases(values.channel)[loadingPhraseIndex]}
              </p>
            )}
          </form>
        </div>
        {activeCoachingConfig && (
          <div className="coaching-selfservice-side-col">
            <Card compact>
              <h3 className="flex-align-center small-gap" data-testid="ai-coaching-header">
                <IconSparkles className="status-magical icon-svg" /> AI Coaching
              </h3>
              <p>
                This agent is currently enrolled in AI Coaching with{' '}
                {moment(activeCoachingConfig.expiration_date).fromNow(true)} remaining.{' '}
              </p>
              <Link to={`/coaching/inbox/?id=${activeCoachingConfig.task_id}`}>
                {activeCoachingConfig.coaching_question}
              </Link>
            </Card>
          </div>
        )}
      </div>
      <SuggestedQuestions
        initialCategory="sales"
        feature={RECENT_QUESTIONS_COACHING_FEATURE_NAME}
        questions={getCoachingQuestionsWithChannel(values.channel)}
        onQuestionClick={(value) => setFieldValue('question', value)}
      />
    </>
  )
}
export const CoachingSelfServiceForm = CoachingSelfServiceFormComponent
